import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useInfiniteQuery } from '@tanstack/react-query';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ComboBoxField } from '@epoch-ui/components/Form/fields/ComboBoxField/ComboBoxField';
import { Skeleton } from '@epoch-ui/components/Skeleton/skeleton';
import { SearchInput } from '@epoch-ui/components/SearchInput/SearchInput';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { SLACK_REVAMP, SLACK_ENTERPRISE_SHOW_SHARED } from 'constants/featureFlags';
import { companySlackApi, ListSlackChannelsParams } from 'api';

import { useEditableEvent } from '../../../hooks/useEditableEvent';

import { SlackSettingsFormValues } from './slackSettingsSchema';
import { SlackWorkspaceSelector } from './SlackWorkspaceSelector';

const PER_PAGE = 20;

const SkeletonLoader: React.FC<{ count: number }> = ({ count }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={`skeleton-${index}`}
        className="tw-flex tw-min-h-[32px] tw-items-center tw-px-2 tw-py-1.5"
      >
        <Skeleton className="tw-h-5 tw-w-full tw-rounded-sm" />
      </div>
    ))}
  </>
);

interface ConnectExistingChannelComponentProps {
  disabled?: boolean;
}

export const ConnectExistingChannelComponent: React.FC<ConnectExistingChannelComponentProps> = ({
  disabled = false
}) => {
  const { watch, setValue } = useFormContext<SlackSettingsFormValues>();
  const isSlackRevampEnabled = useFeatureFlag(SLACK_REVAMP);
  const isEnterpriseSharedEnabled = useFeatureFlag(SLACK_ENTERPRISE_SHOW_SHARED);
  const { slackChannelName } = useEditableEvent();

  const selectedTeamId = watch('slackTeamId');

  const [searchChannelText, setSearchChannelText] = useState('');
  // Track if workspace change was triggered by selecting a channel from another workspace
  const workspaceChangedByChannelSelectionRef = useRef(false);

  // Clear search text when workspace changes (but not when triggered by cross-workspace channel selection)
  useEffect(() => {
    if (!workspaceChangedByChannelSelectionRef.current) {
      setSearchChannelText('');
    }
    workspaceChangedByChannelSelectionRef.current = false;
  }, [selectedTeamId]);

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        'dashboard',
        'editEventSettings',
        'slackChannels',
        searchChannelText,
        selectedTeamId,
        isSlackRevampEnabled
      ],
      queryFn: async ({ pageParam = 0 }) => {
        const params: ListSlackChannelsParams = {
          search: searchChannelText,
          page: pageParam,
          perPage: PER_PAGE
        };

        if (isSlackRevampEnabled && selectedTeamId) {
          params.slackTeamId = selectedTeamId;
        }

        return companySlackApi.listChannels(params);
      },
      getNextPageParam: (lastPage, pages) => {
        const totalFetched = pages.reduce((sum, page) => sum + page.channels.length, 0);
        if (totalFetched < lastPage.totalCount) {
          return pages.length; // Next page number
        }
        return undefined;
      },
      enabled: !isSlackRevampEnabled || !!selectedTeamId,
      initialPageParam: 0
    });

  const existingSlackChannels = useMemo(() => {
    return data?.pages.flatMap((page) => page.channels) || [];
  }, [data?.pages]);

  // Extract other workspace channels when main results are empty (only when enterprise flag is enabled)
  const otherWorkspaceChannels = useMemo(() => {
    if (!isEnterpriseSharedEnabled) return [];
    const lastPage = data?.pages[data.pages.length - 1];
    // Only show if main channels are empty and other workspace channels exist
    if (existingSlackChannels.length === 0 && lastPage?.otherWorkspaceChannels) {
      return lastPage.otherWorkspaceChannels;
    }
    return [];
  }, [data?.pages, existingSlackChannels.length, isEnterpriseSharedEnabled]);

  const isLoadingChannels = isLoading;

  const channelItems = useMemo(() => {
    return existingSlackChannels.map((channel) => (
      <ComboBoxField.Item
        key={channel.channelId}
        value={channel.channelId}
        onSelect={() => {
          setValue('slackChannelName', channel.channelName);
        }}
      >
        <span className="tw-overflow-hidden tw-truncate tw-whitespace-nowrap tw-text-sm tw-font-normal tw-leading-5 tw-text-dark-black">
          {channel.channelName}
        </span>
      </ComboBoxField.Item>
    ));
  }, [existingSlackChannels]);

  // Render other workspace channels with workspace name suffix
  const otherWorkspaceChannelItems = useMemo(() => {
    if (!otherWorkspaceChannels.length) return null;

    return (
      <>
        <div className="tw-px-2 tw-py-1.5 tw-text-xs tw-font-medium tw-text-primary-grey">
          Channels from shared workspaces
        </div>
        {otherWorkspaceChannels.map((channel) => (
          <ComboBoxField.Item
            key={channel.channelId}
            value={channel.channelId}
            onSelect={() => {
              setValue('slackChannelName', channel.channelName);
              // Auto-switch workspace when selecting a cross-workspace channel
              if (isEnterpriseSharedEnabled && channel.teamId) {
                workspaceChangedByChannelSelectionRef.current = true;
                setValue('slackTeamId', channel.teamId);
              }
            }}
          >
            <span className="tw-overflow-hidden tw-truncate tw-whitespace-nowrap tw-text-sm tw-font-normal tw-leading-5 tw-text-dark-black">
              {channel.channelName} ({channel.slackTeamName || 'Enterprise/Shared'})
            </span>
          </ComboBoxField.Item>
        ))}
      </>
    );
  }, [otherWorkspaceChannels, isEnterpriseSharedEnabled, setValue]);

  const emptyState = useMemo(() => {
    if (isSlackRevampEnabled && !selectedTeamId) {
      return <ComboBoxField.Empty>Please select a workspace first</ComboBoxField.Empty>;
    }

    if (isError) {
      return <ComboBoxField.Empty>Failed to load channels. Please try again.</ComboBoxField.Empty>;
    }

    // Only show "No channels found" if both main and other workspace channels are empty
    if (!existingSlackChannels.length && !otherWorkspaceChannels.length && !isLoadingChannels) {
      return <ComboBoxField.Empty>No channels found.</ComboBoxField.Empty>;
    }

    return null;
  }, [
    isSlackRevampEnabled,
    selectedTeamId,
    isError,
    existingSlackChannels.length,
    otherWorkspaceChannels.length,
    isLoadingChannels
  ]);

  const getTriggerContent = useCallback(
    (channelId: string): React.ReactNode => {
      if (channelId) {
        // First check in main channels
        let selectedChannel = existingSlackChannels.find((c) => c.channelId === channelId);
        // If not found, check in other workspace channels
        if (!selectedChannel && otherWorkspaceChannels.length > 0) {
          selectedChannel = otherWorkspaceChannels.find((c) => c.channelId === channelId);
        }
        return (
          <span className="tw-text-sm tw-text-dark-black">
            #{selectedChannel?.channelName || slackChannelName || 'Select'}
          </span>
        );
      }
      return 'Select';
    },
    [existingSlackChannels, otherWorkspaceChannels, slackChannelName]
  );

  return (
    <div>
      {isSlackRevampEnabled && <SlackWorkspaceSelector disabled={disabled} />}
      <ComboBoxField
        name="slackChannelId"
        label="Slack Channel"
        triggerContent={getTriggerContent}
        className="tw-mb-2"
        disabled={disabled}
        contentAvoidCollisions
      >
        <ComboBoxField.Command shouldFilter={false}>
          <div className="tw-p-2">
            <SearchInput
              value={searchChannelText}
              onChange={(e) => setSearchChannelText(e.target.value)}
              placeholder="Search channels..."
              className="tw-w-full"
              debounce={300}
            />
          </div>
          <ComboBoxField.List className="tw-mt-1 !tw-max-h-none !tw-overflow-visible">
            <div
              id="slack-channels-scrollable"
              className="tw-max-h-[300px] tw-overflow-y-auto tw-overflow-x-hidden"
            >
              {((): React.ReactNode => {
                if (isLoadingChannels && !existingSlackChannels.length) {
                  return <SkeletonLoader count={5} />;
                }

                if (existingSlackChannels.length > 0) {
                  return (
                    <InfiniteScroll
                      dataLength={existingSlackChannels.length}
                      next={fetchNextPage}
                      hasMore={hasNextPage ?? false}
                      loader={
                        isFetchingNextPage && (
                          <div className="tw-space-y-1">
                            <SkeletonLoader count={3} />
                          </div>
                        )
                      }
                      scrollableTarget="slack-channels-scrollable"
                      className="tw-flex tw-flex-col"
                    >
                      {channelItems}
                    </InfiniteScroll>
                  );
                }

                // Show other workspace channels when main results are empty
                if (otherWorkspaceChannelItems) {
                  return otherWorkspaceChannelItems;
                }

                return emptyState;
              })()}
            </div>
          </ComboBoxField.List>
        </ComboBoxField.Command>
      </ComboBoxField>
      <span className="tw-text-xs tw-font-normal tw-leading-4 tw-text-primary-grey">
        Can&apos;t find a channel?&nbsp;
        <a
          href="https://help.epochapp.com/articles/how-do-i-connect-an-existing-slack-channel-to-my-event"
          target="_blank"
          rel="noopener noreferrer"
          className="tw-text-primary-grey tw-underline hover:tw-no-underline"
        >
          Learn more
        </a>
      </span>
    </div>
  );
};
